import { useEffect, useState, useCallback, useRef } from 'react';
import { Youtube, Loader2, Link2, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import api from '@/lib/api';
import { disconnectYouTube, useYouTubeAccountReset } from '@/lib/youtubeConnection';
import { formatApiError } from '@/lib/formatApiError';
import { autofillFromUploadedVideo, watchDiscoverySeo } from '@/lib/publishVideoAutofill';
import { isDesktopApp } from '@/services/config/env';
import { openYoutubeOAuthWindow, subscribeYoutubeOAuthResult } from '@/lib/youtubeOAuthPopup';
import {
  applyShortsDescriptionBoost,
  applyYoutubePublishDraft,
  buildYoutubePublishDraft,
  clearYoutubePublishDraft,
  loadYoutubePublishDraft,
  resolveYoutubePublishLanguage,
  saveYoutubePublishDraft,
} from '@/lib/youtubePublishDraft';
import { PublishThumbnailPicker } from '@/components/social/PublishThumbnailPicker';
import { PublishTagChips, parsePublishTags } from '@/components/social/PublishTagChips';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const DEFAULT_CATEGORIES = [
  { id: '22', label: 'People & Blogs' },
  { id: '27', label: 'Education' },
  { id: '24', label: 'Entertainment' },
  { id: '23', label: 'Comedy' },
];

/**
 * YouTube OAuth + upload form (same fields as POST /api/social-publish/youtube/upload).
 */
export function YouTubePublishDialog({
  open,
  onOpenChange,
  oauthReturnPath,
  defaultVideoUrl = '',
  defaultTitle = '',
  scriptContext = '',
  tone = '',
  visualStyle = '',
  narrationProjectId = null,
  storyId = null,
  /** Pre-check Shorts when the export is vertical (e.g. 9:16 story). */
  defaultShortsBoost = false,
  /** Story / plan language (e.g. `hi`) — preselects Optimize language + YT default/audio language. */
  storyLanguage = '',
  onPublishStart,
  onPublished,
  onPublishFailed,
}) {
  const [status, setStatus] = useState({ connected: false, channel_title: null });
  const [statusLoading, setStatusLoading] = useState(false);
  const [beginBusy, setBeginBusy] = useState(false);
  const [disconnectBusy, setDisconnectBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [aiSuggestBusy, setAiSuggestBusy] = useState(false);
  const [aiSuggestNote, setAiSuggestNote] = useState('');
  const [thumbFrameUrls, setThumbFrameUrls] = useState([]);
  const [aiThumbUrls, setAiThumbUrls] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  const [videoUrl, setVideoUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tagsRaw, setTagsRaw] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [moreTagsBusy, setMoreTagsBusy] = useState(false);
  const [categoryId, setCategoryId] = useState('22');
  const [privacyStatus, setPrivacyStatus] = useState('private');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [defaultLanguage, setDefaultLanguage] = useState('');
  const [defaultAudioLanguage, setDefaultAudioLanguage] = useState('');
  /** AI optimize output language (title/description/tags tone); drives suggest-metadata `language`. */
  const [metadataOptimizeLang, setMetadataOptimizeLang] = useState('en');
  /** Shorts-oriented AI prompt + upload-time #Shorts / tag boost. */
  const [youtubeShortsMode, setYoutubeShortsMode] = useState(false);
  const [playlistId, setPlaylistId] = useState('');
  const [notifySubscribers, setNotifySubscribers] = useState(false);
  const [embeddable, setEmbeddable] = useState(true);
  const [license, setLicense] = useState('youtube');
  const [madeForKids, setMadeForKids] = useState(false);
  const [oauthWaiting, setOauthWaiting] = useState(false);
  const oauthPopupRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/social-publish/youtube/status');
      const next = {
        connected: Boolean(data.connected),
        channel_title: data.channel_title || null,
        channel_id: data.channel_id || null,
      };
      setStatus(next);
      return next;
    } catch {
      const next = { connected: false, channel_title: null, channel_id: null };
      setStatus(next);
      return next;
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useYouTubeAccountReset(loadStatus);

  useEffect(() => {
    if (!open) return undefined;
    setStatusLoading(true);
    loadStatus();
    return undefined;
  }, [open, loadStatus]);

  useEffect(() => {
    if (!open) return;
    const draft = loadYoutubePublishDraft();
    if (draft) {
      applyYoutubePublishDraft(draft, {
        setVideoUrl, setTitle, setDescription, setTagsRaw, setCategoryId, setPrivacyStatus,
        setThumbnailUrl, setDefaultLanguage, setDefaultAudioLanguage, setMetadataOptimizeLang,
        setYoutubeShortsMode, setPlaylistId, setNotifySubscribers, setEmbeddable, setLicense,
        setMadeForKids, setThumbFrameUrls,
      });
    } else {
      setVideoUrl(defaultVideoUrl || '');
      setTitle(defaultTitle || '');
      setYoutubeShortsMode(Boolean(defaultShortsBoost));
    }
    // Story language wins for AI optimize + YouTube language fields (e.g. Hindi stories → hi).
    const resolved = resolveYoutubePublishLanguage(storyLanguage);
    if (resolved) {
      setMetadataOptimizeLang(resolved.optimizeLang);
      setDefaultLanguage(resolved.defaultLanguage);
      setDefaultAudioLanguage(resolved.defaultAudioLanguage);
    }
  }, [open, defaultVideoUrl, defaultTitle, defaultShortsBoost, storyLanguage]);

  useEffect(() => {
    if (!open) {
      setThumbFrameUrls([]);
      setAiThumbUrls([]);
    }
  }, [open]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/social-publish/youtube/categories');
        if (Array.isArray(data.categories) && data.categories.length) {
          setCategories(data.categories.map((c) => ({ id: c.id, label: `${c.label} (${c.id})` })));
        }
      } catch {
        /* defaults */
      }
    })();
  }, []);

  const collectFormDraft = useCallback(
    () =>
      buildYoutubePublishDraft({
        videoUrl,
        title,
        description,
        tagsRaw,
        categoryId,
        privacyStatus,
        thumbnailUrl,
        defaultLanguage,
        defaultAudioLanguage,
        metadataOptimizeLang,
        youtubeShortsMode,
        playlistId,
        notifySubscribers,
        embeddable,
        license,
        madeForKids,
        thumbFrameUrls,
      }),
    [
      videoUrl,
      title,
      description,
      tagsRaw,
      categoryId,
      privacyStatus,
      thumbnailUrl,
      defaultLanguage,
      defaultAudioLanguage,
      metadataOptimizeLang,
      youtubeShortsMode,
      playlistId,
      notifySubscribers,
      embeddable,
      license,
      madeForKids,
      thumbFrameUrls,
    ],
  );

  const refreshStatusAfterConnect = useCallback(async () => {
    let last = { connected: false, channel_title: null, channel_id: null };
    for (let attempt = 0; attempt < 8; attempt += 1) {
      if (attempt > 0) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, 500);
        });
      }
      last = await loadStatus();
      if (last?.connected) return last;
    }
    return last;
  }, [loadStatus]);

  const resolveOAuthFrontendOrigin = useCallback(() => {
    const origin = window.location.origin;
    if (origin && origin !== 'null') return origin;
    if (isDesktopApp()) return 'file://';
    return origin;
  }, []);

  const handleOAuthResult = useCallback(
    async ({ status, message }) => {
      setOauthWaiting(false);
      setBeginBusy(false);
      oauthPopupRef.current = null;
      if (status === 'connected') {
        const next = await refreshStatusAfterConnect();
        if (next?.connected) {
          toast.success(
            next.channel_title
              ? `YouTube connected: ${next.channel_title}`
              : 'YouTube connected.',
          );
          clearYoutubePublishDraft();
          return;
        }
        toast.warning(
          'Google sign-in finished, but YouTube is not saved yet. Check the backend on port 8000, then try again.',
        );
        return;
      }
      if (
        message === 'oauth_popup_closed' ||
        message === 'callback_load_failed' ||
        message === 'token_exchange' ||
        message === 'oauth_code_invalid'
      ) {
        const recovered = await refreshStatusAfterConnect();
        if (recovered?.connected) {
          toast.success(
            recovered.channel_title
              ? `YouTube connected: ${recovered.channel_title}`
              : 'YouTube connected.',
          );
          clearYoutubePublishDraft();
          return;
        }
      }
      const oauthErrors = {
        token_exchange:
          'Google token exchange failed. Restart the backend (rebuild desktop app if packaged), then connect again.',
        oauth_pkce_mismatch:
          'OAuth session expired. Click Connect YouTube again (one sign-in attempt only).',
        oauth_code_invalid:
          'Sign-in link expired or was used twice. Close the popup and click Connect YouTube again.',
        oauth_redirect_mismatch:
          'Redirect URI mismatch. Use http://localhost:8000/.../oauth/callback in Google Console and one backend on port 8000.',
        oauth_callback_unreadable:
          'Could not read the OAuth result page. Check backend logs and try again.',
      };
      const detail =
        message === 'oauth_popup_closed'
          ? 'Sign-in window closed before Twin Tales received the result. Restart the desktop app and try again.'
          : message === 'callback_load_failed'
            ? 'Could not load the OAuth callback. Stop uvicorn on port 8000, restart Twin Tales only, then try Connect again.'
            : oauthErrors[message] ||
              (message && message !== 'oauth_error' ? message : 'YouTube connection failed');
      toast.error(detail);
      await loadStatus();
    },
    [loadStatus, refreshStatusAfterConnect],
  );

  useEffect(() => {
    if (!open || isDesktopApp()) return undefined;
    return subscribeYoutubeOAuthResult({
      popupRef: oauthPopupRef,
      onResult: (payload) => {
        handleOAuthResult(payload);
      },
      onPopupClosed: () => {
        if (!oauthWaiting) return;
        setOauthWaiting(false);
        setBeginBusy(false);
        oauthPopupRef.current = null;
      },
    });
  }, [open, oauthWaiting, handleOAuthResult]);

  useEffect(() => {
    if (open) return undefined;
    window.twinTales?.cancelOAuthPopupResultWait?.();
    return undefined;
  }, [open]);

  const handleConnectYouTube = async () => {
    setBeginBusy(true);
    let openedPopup = false;
    saveYoutubePublishDraft(collectFormDraft());

    try {
      if (isDesktopApp()) {
        try {
          const { data: desk } = await api.get('/desktop/config');
          if (!desk?.youtube_oauth_configured) {
            toast.error(
              'YouTube OAuth is missing in the desktop backend .env. Run: bash desktop-app/scripts/install-desktop-env.sh — then restart Twin Tales.',
              { duration: 12000 },
            );
            return;
          }
        } catch (deskErr) {
          toast.error(
            formatApiError(
              deskErr,
              'Could not reach the desktop backend. Quit other servers on port 8000 and restart Twin Tales.',
            ),
          );
          return;
        }
      }

      const path = oauthReturnPath || `${window.location.pathname}${window.location.search}`;
      const { data } = await api.post('/social-publish/youtube/oauth/begin', {
        return_path: path.startsWith('/') ? path : `/${path}`,
        oauth_mode: 'popup',
        frontend_origin: resolveOAuthFrontendOrigin(),
      });

      if (!data?.authorization_url) {
        toast.error('Could not start YouTube login (no authorization URL).');
        return;
      }

      const desktopOAuthWait =
        isDesktopApp() && typeof window.twinTales?.waitForOAuthPopupResult === 'function'
          ? window.twinTales.waitForOAuthPopupResult()
          : null;

      setOauthWaiting(true);
      const popup = openYoutubeOAuthWindow(data.authorization_url);
      if (!popup && !desktopOAuthWait) {
        window.twinTales?.cancelOAuthPopupResultWait?.();
        toast.error(
          isDesktopApp()
            ? 'Could not open sign-in window. Quit and restart the Twin Tales desktop app.'
            : 'Allow pop-ups for this site to connect YouTube, or try again.',
        );
        setOauthWaiting(false);
        return;
      }

      oauthPopupRef.current = popup;
      openedPopup = true;
      toast.info('Complete Google sign-in in the popup window (not an external browser tab).');

      if (desktopOAuthWait) {
        const payload = await desktopOAuthWait;
        await handleOAuthResult({
          status: String(payload?.status || ''),
          message: String(payload?.message || ''),
        });
      }
    } catch (err) {
      window.twinTales?.cancelOAuthPopupResultWait?.();
      toast.error(
        formatApiError(
          err,
          'OAuth not configured or failed. Check backend/.env (YouTube keys) and restart the app.',
        ),
        { duration: 10000 },
      );
    } finally {
      if (!openedPopup) {
        setOauthWaiting(false);
        setBeginBusy(false);
      }
    }
  };

  const handleDisconnect = async () => {
    setDisconnectBusy(true);
    try {
      await disconnectYouTube();
      toast.success('YouTube disconnected');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Disconnect failed');
    } finally {
      setDisconnectBusy(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!videoUrl.trim()) {
      toast.error('Video URL is required');
      return;
    }
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    const tags = parsePublishTags(tagsRaw);

    setUploadBusy(true);
    onPublishStart?.();
    try {
      const { data } = await api.post('/social-publish/youtube/upload', {
        video_url: videoUrl.trim(),
        title: title.trim(),
        description: description.trim(),
        tags,
        category_id: categoryId,
        privacy_status: privacyStatus,
        thumbnail_url: thumbnailUrl.trim() || null,
        default_language: defaultLanguage.trim() || null,
        default_audio_language: defaultAudioLanguage.trim() || null,
        playlist_id: playlistId.trim() || null,
        notify_subscribers: notifySubscribers,
        embeddable,
        license,
        made_for_kids: madeForKids,
        shorts_metadata_boost: youtubeShortsMode,
        narration_project_id: narrationProjectId || null,
        story_id: storyId || null,
      });
      const shortsLink = data.shorts_url || (youtubeShortsMode && data.video_id
        ? `https://www.youtube.com/shorts/${data.video_id}`
        : '');
      toast.success(shortsLink ? `Uploaded as Short: ${shortsLink}` : `Uploaded: ${data.url}`);
      clearYoutubePublishDraft();
      if (Array.isArray(data.warnings) && data.warnings.length) {
        data.warnings.forEach((w) => toast.warning(w));
      }
      onPublished?.({
        url: shortsLink || data.url || '',
        id: data.video_id || null,
        shortsUrl: shortsLink || null,
      });
      onOpenChange(false);
    } catch (err) {
      if (err.response?.status === 401) {
        await loadStatus();
      }
      const message = formatApiError(err, 'Upload failed');
      toast.error(message);
      onPublishFailed?.(message);
    } finally {
      setUploadBusy(false);
    }
  };

  const handleMoreTags = async () => {
    setMoreTagsBusy(true);
    try {
      const existing = [...parsePublishTags(tagsRaw), ...tagSuggestions];
      const { data } = await api.post('/social-publish/youtube/suggest-tags', {
        title: (title || '').trim().slice(0, 120),
        description: (description || scriptContext || '').trim().slice(0, 2000),
        existing_tags: existing,
        language: (defaultLanguage || metadataOptimizeLang || 'en').trim().slice(0, 12) || 'en',
        count: 8,
      });
      const extra = Array.isArray(data.tags) ? data.tags.map((tag) => String(tag || '').trim()).filter(Boolean) : [];
      if (!extra.length) {
        toast.info('No extra tags found. Try Autofill first.');
        return;
      }
      const have = new Set(existing.map((tag) => tag.toLocaleLowerCase()));
      setTagSuggestions((prev) => {
        const next = [...prev];
        extra.forEach((tag) => {
          if (!have.has(tag.toLocaleLowerCase())) {
            have.add(tag.toLocaleLowerCase());
            next.push(tag);
          }
        });
        return next;
      });
    } catch (err) {
      toast.error(formatApiError(err, 'Could not suggest more tags'));
    } finally {
      setMoreTagsBusy(false);
    }
  };

  const handleAiSuggestMetadata = async () => {
    setAiSuggestBusy(true);
    setAiSuggestNote('');
    try {
      const hint = (title || defaultTitle || '').trim().slice(0, 300);
      const uploaded = videoUrl.trim();
      let data;
      if (uploaded) {
        setAiSuggestNote('Queued — transcribing the video…');
        data = await autofillFromUploadedVideo(
          {
            video_url: uploaded,
            platform: 'youtube',
            title_hint: '',
            script_text: '',
            tone: (tone || '').trim().slice(0, 200) || null,
            visual_style: (visualStyle || '').trim().slice(0, 200) || null,
            language: (metadataOptimizeLang || 'en').trim().slice(0, 12) || 'en',
            optimize_for_shorts: Boolean(youtubeShortsMode),
          },
          { onProgress: (row) => setAiSuggestNote(row.message || 'Working…') },
        );
      } else {
        const response = await api.post('/social-publish/youtube/suggest-metadata', {
          title_hint: hint,
          script_text: (scriptContext || '').trim().slice(0, 14000),
          tone: (tone || '').trim().slice(0, 200) || null,
          visual_style: (visualStyle || '').trim().slice(0, 200) || null,
          language: (metadataOptimizeLang || 'en').trim().slice(0, 12) || 'en',
          optimize_for_shorts: Boolean(youtubeShortsMode),
        });
        data = response.data;
      }
      const appliedTitle = data.title ? String(data.title).slice(0, 100) : hint;
      const appliedDescription =
        data.description != null ? String(data.description).slice(0, 5000) : description;
      if (data.title) setTitle(appliedTitle);
      if (data.description != null) setDescription(appliedDescription);
      if (Array.isArray(data.tags) && data.tags.length) {
        setTagsRaw(data.tags.join(', '));
      }
      if (Array.isArray(data.keywords) && data.keywords.length) {
        const selectedKeys = new Set((data.tags || []).map((tag) => String(tag).trim().toLocaleLowerCase()));
        setTagSuggestions(
          data.keywords
            .map((tag) => String(tag || '').trim())
            .filter((tag) => tag && !selectedKeys.has(tag.toLocaleLowerCase())),
        );
      } else {
        setTagSuggestions([]);
      }
      if (data.category_id) setCategoryId(String(data.category_id));
      if (data.default_language) setDefaultLanguage(String(data.default_language).slice(0, 12));
      if (data.default_audio_language) setDefaultAudioLanguage(String(data.default_audio_language).slice(0, 12));
      const tipLine = Array.isArray(data.seo_tips) && data.seo_tips.length
        ? data.seo_tips.slice(0, 3).join(' ')
        : '';
      const keywordLine = Array.isArray(data.keywords) && data.keywords.length
        ? `Keywords: ${data.keywords.slice(0, 6).join(', ')}.`
        : '';
      const scoreBit = Number.isFinite(Number(data.seo_score)) ? `SEO score ${data.seo_score}/100.` : '';
      setAiSuggestNote([tipLine, keywordLine, scoreBit].filter(Boolean).join(' '));
      setAiSuggestBusy(false);
      if (uploaded) {
        const baseline = {
          title: appliedTitle,
          description: appliedDescription,
          tags: Array.isArray(data.tags) ? data.tags.join(', ') : tagsRaw,
        };
        watchDiscoverySeo(uploaded, {
          onPack: (seo) => {
            if (seo.title) setTitle((current) => (current === baseline.title ? String(seo.title).slice(0, 100) : current));
            if (seo.description) {
              setDescription((current) => (current === baseline.description ? String(seo.description).slice(0, 5000) : current));
            }
            if (Array.isArray(seo.tags) && seo.tags.length) {
              const nextTags = seo.tags.join(', ');
              setTagsRaw((current) => (current === baseline.tags ? nextTags : current));
            }
          },
        });
      }
      toast.success(`Form autofilled with AI.${uploaded ? ' Used the video transcript.' : ''} Review title, thumbnail, Made for kids, and privacy before uploading.`);
      if (appliedTitle.trim()) {
        const infoLine = (appliedDescription || scriptContext || '').trim().split('\n')[0]?.slice(0, 200) || '';
        const excerpt = (data.transcript || scriptContext || appliedDescription || '').trim().slice(0, 2000);
        void (async () => {
          try {
            const { data: thumbData } = await api.post('/social-publish/ai-thumbnail-options', {
              platform: 'youtube',
              title: appliedTitle.trim().slice(0, 120),
              info_line: infoLine.slice(0, 280),
              script_excerpt: excerpt,
              tone: (tone || '').trim().slice(0, 200) || null,
              visual_style: (visualStyle || '').trim().slice(0, 200) || null,
              count: 3,
            });
            const urls = Array.isArray(thumbData.urls) ? thumbData.urls.filter(Boolean) : [];
            setAiThumbUrls(urls);
            if (urls[0]) setThumbnailUrl(urls[0]);
          } catch {
            setAiThumbUrls([]);
          }
        })();
      }
    } catch (err) {
      if (!err?.cancelled) {
        setAiSuggestNote('');
        toast.error(formatApiError(err, 'AI suggestion failed'));
      }
    } finally {
      setAiSuggestBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto flex flex-col gap-0 sm:max-w-xl"
        style={{ background: 'var(--bg-primary)', borderColor: 'var(--glass-border)' }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Youtube className="w-5 h-5 text-red-600 shrink-0" />
            Upload to YouTube
          </DialogTitle>
          <DialogDescription style={{ color: 'var(--text-secondary)' }}>
            Connect your channel once, then fill in metadata. The server downloads your video URL and uploads via YouTube Data
            API. Choose metadata language (English or Hindi), optionally turn on Shorts mode, then use{' '}
            <strong>Autofill using AI</strong> for metadata, tags, and story-style thumbnails — always verify accuracy before publishing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 border-t border-b" style={{ borderColor: 'var(--glass-border)' }}>
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
            Authentication
          </p>
          {statusLoading ? (
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Checking…
            </div>
          ) : status.connected ? (
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                Connected: <strong>{status.channel_title || 'your channel'}</strong>
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disconnectBusy}
                onClick={handleDisconnect}
                className="rounded-lg gap-1"
              >
                {disconnectBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                Disconnect
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Button
                type="button"
                onClick={handleConnectYouTube}
                disabled={beginBusy || oauthWaiting}
                className="rounded-lg gap-2 bg-red-600 hover:bg-red-700 text-white"
              >
                {beginBusy || oauthWaiting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Connect YouTube (Google)
              </Button>
              {oauthWaiting ? (
                <p className="text-[11px] leading-snug" style={{ color: 'var(--text-tertiary)' }}>
                  Sign in with Google in the popup tab. This dialog keeps your video URL and metadata.
                </p>
              ) : null}
            </div>
          )}
        </div>

        <div className="rounded-lg border px-3 py-2.5 space-y-3" style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Optimize / title language</Label>
              <Select value={metadataOptimizeLang} onValueChange={setMetadataOptimizeLang}>
                <SelectTrigger className="rounded-lg text-sm h-9" data-testid="youtube-metadata-lang-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="hi">Hindi (हिंदी)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] leading-snug" style={{ color: 'var(--text-tertiary)' }}>
                Hindi fills title &amp; description in Devanagari and adds India-style searchable tags when you run AI optimize.
              </p>
            </div>
            <label className="flex flex-col gap-2 rounded-lg border px-3 py-2 cursor-pointer" style={{ borderColor: 'var(--glass-border)' }}>
              <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>YouTube Shorts</span>
              <span className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                <input
                  type="checkbox"
                  checked={youtubeShortsMode}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setYoutubeShortsMode(on);
                    if (on) {
                      setDescription((prev) => applyShortsDescriptionBoost(prev));
                    }
                  }}
                  className="rounded border shrink-0"
                  aria-label="YouTube Shorts mode for AI optimize and upload hashtags"
                />
                Adds #Shorts to title and description, Shorts tags, and opens the Shorts player link after upload.
              </span>
              <span className="text-[10px] leading-snug" style={{ color: 'var(--text-tertiary)' }}>
                YouTube classifies by vertical layout (~9:16) and length (≤60s). watch?v= links can look like a normal video until processing finishes — use the Shorts URL we show after upload.
              </span>
            </label>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            type="button"
            size="sm"
            className="rounded-lg gap-1.5 font-semibold shadow-md ring-2 ring-[#2DD4BF]/80 ring-offset-2 ring-offset-[var(--bg-primary,#0b0e14)] bg-[#2DD4BF] text-[#06080D] hover:bg-[#5EEAD4]"
            disabled={aiSuggestBusy}
            onClick={handleAiSuggestMetadata}
            data-testid="youtube-ai-suggest-metadata-btn"
            title="Recommended — fills title, description, tags, and thumbnails from your story"
          >
            {aiSuggestBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <TwinneeLogo className="w-4 h-4" />}
            Autofill using AI
          </Button>
          <span className="text-[11px] leading-snug" style={{ color: 'var(--text-tertiary)' }}>
            {aiSuggestNote
              ? aiSuggestNote
              : `Recommended next step — fills title, description, tags, and 3 story-style thumbnails${metadataOptimizeLang === 'hi' ? ' in Hindi' : ''}. With a video URL, Autofill transcribes the audio first.`}
          </span>
        </div>

        <form onSubmit={handleUpload} className="space-y-3 pt-2">
          <div className="space-y-1">
            <Label htmlFor="yt-v-url">Video URL *</Label>
            <Input
              id="yt-v-url"
              placeholder="HTTPS URL to MP4 (e.g. exported file or final stitched URL)"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              className="rounded-lg text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="yt-title">Title * (max 100)</Label>
            <Input id="yt-title" maxLength={100} value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-lg text-sm" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="yt-desc">Description</Label>
            <textarea
              id="yt-desc"
              rows={4}
              maxLength={5000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-lg border px-2 py-1.5 text-sm bg-transparent"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
            />
          </div>
          <div className="space-y-1">
            <Label>Tags</Label>
            <PublishTagChips
              value={tagsRaw}
              suggestions={tagSuggestions}
              onChange={setTagsRaw}
              onSuggestionsChange={setTagSuggestions}
              moreBusy={moreTagsBusy}
              onMore={handleMoreTags}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label>Category</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger className="rounded-lg text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Privacy</Label>
              <Select value={privacyStatus} onValueChange={setPrivacyStatus}>
                <SelectTrigger className="rounded-lg text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private</SelectItem>
                  <SelectItem value="unlisted">Unlisted</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <PublishThumbnailPicker
            platform="youtube"
            videoUrl={videoUrl}
            title={title}
            description={description}
            scriptContext={scriptContext}
            tone={tone}
            visualStyle={visualStyle}
            thumbnailUrl={thumbnailUrl}
            onThumbnailUrlChange={setThumbnailUrl}
            frameUrls={thumbFrameUrls}
            onFrameUrlsChange={setThumbFrameUrls}
            aiUrls={aiThumbUrls}
            onAiUrlsChange={setAiThumbUrls}
            showAiGenerateButton={false}
            helpText="Story thumbnails are created when you click Autofill using AI above. You can also sample video frames, upload an image, or paste a URL."
            label="Thumbnail (JPEG/PNG)"
            testIdPrefix="youtube-thumb"
          />
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="yt-lang">Default language</Label>
              <Input id="yt-lang" placeholder="en" value={defaultLanguage} onChange={(e) => setDefaultLanguage(e.target.value)} className="rounded-lg text-sm" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="yt-alang">Audio language</Label>
              <Input id="yt-alang" placeholder="en" value={defaultAudioLanguage} onChange={(e) => setDefaultAudioLanguage(e.target.value)} className="rounded-lg text-sm" />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="yt-pl">Playlist ID (optional)</Label>
            <Input id="yt-pl" value={playlistId} onChange={(e) => setPlaylistId(e.target.value)} className="rounded-lg text-sm" />
          </div>
          <div className="space-y-1">
            <Label>License</Label>
            <Select value={license} onValueChange={setLicense}>
              <SelectTrigger className="rounded-lg text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="youtube">Standard YouTube license</SelectItem>
                <SelectItem value="creativeCommon">Creative Commons — Attribution</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={notifySubscribers} onChange={(e) => setNotifySubscribers(e.target.checked)} className="rounded border" />
            Notify subscribers (only applies when privacy is public)
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={embeddable} onChange={(e) => setEmbeddable(e.target.checked)} className="rounded border" />
            Allow embedding
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={madeForKids} onChange={(e) => setMadeForKids(e.target.checked)} className="rounded border" />
            Made for kids (accurate declaration required)
          </label>

          <DialogFooter className="gap-2 pt-2 flex-col sm:flex-row">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={uploadBusy || !status.connected}
              className="rounded-lg gap-2 bg-red-600 hover:bg-red-700 text-white"
            >
              {uploadBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Youtube className="w-4 h-4" />}
              Upload
            </Button>
          </DialogFooter>
          {!status.connected && (
            <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>Connect YouTube above to enable upload.</p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
